import type { CSSProperties } from "react";
import { simulate, type VisionMode } from "@/color/cvd";
import type { Hex } from "@/color/oklch";
import { XTERM_EXTENDED } from "@/color/xterm256";
import { makeContext } from "@/exporters/types";
import { WEECHAT_ANSI_NAMES, weechatColor } from "@/exporters/weechat";
import { type ColorId, hexOf, type Resolved } from "@/model/resolve";
import type { Theme } from "@/model/theme";
import { SYNTAX_KEYS, tokensOf } from "@/model/tokens";

export function cssVar(id: string): string {
  return `--t-${id.replace(/[^a-zA-Z0-9-]/g, "_")}`;
}

/** `var(--t-…)` for a palette swatch or token. */
export function v(id: ColorId): string {
  return `var(${cssVar(id)})`;
}

/** WeeChat colors as WeeChat will actually draw them (ANSI names or xterm-256). */
export function w(key: string): string {
  return `var(${cssVar(`w.${key}`)})`;
}

export function previewVars(theme: Theme, resolved: Resolved, vision: VisionMode): CSSProperties {
  const out: Record<string, string> = {};
  const sim = (hex: Hex) => simulate(hex, vision);
  for (const [id, hex] of Object.entries(resolved.colors)) out[cssVar(id)] = sim(hex);
  for (const key of SYNTAX_KEYS) {
    const s = resolved.styles[key];
    out[`--s-${key}-w`] = s.bold ? "700" : "400";
    out[`--s-${key}-i`] = s.italic ? "italic" : "normal";
    out[`--s-${key}-u`] = s.underline ? "underline" : "none";
  }
  // WeeChat can only show ANSI or xterm-256 colors: preview what it will really draw.
  const ctx = makeContext(theme, resolved);
  const ansi = (i: number) => hexOf(resolved, `terminal.ansi${i}`);
  for (const t of tokensOf("weechat")) {
    const c = weechatColor(ctx, hexOf(resolved, t.id), t.role);
    let hex: Hex | undefined;
    if (c.value === "default") {
      hex =
        t.role === "bg"
          ? hexOf(resolved, "terminal.background")
          : hexOf(resolved, "terminal.foreground");
    } else if (WEECHAT_ANSI_NAMES.includes(c.value)) {
      hex = ansi(WEECHAT_ANSI_NAMES.indexOf(c.value));
    } else {
      hex = XTERM_EXTENDED.find((x) => x.index === Number(c.value))?.hex;
    }
    out[cssVar(`w.${t.key}`)] = sim(hex ?? hexOf(resolved, t.id));
  }
  return out as CSSProperties;
}
